import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { client } from '../api/Api';

const ITEMS_PER_PAGE = 24;

const useCategoryData = () => {
    const [categories, setCategories] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [displayedProducts, setDisplayedProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const location = useLocation();

    const extractCategoryFromPath = (pathname: string): string => {
        const segments = pathname.split('/').filter(Boolean);
        return segments[segments.length - 1] || '0';
    };

    const fetchProducts = async (categoryPath: string, page = 0, reset = true) => {
        try {
            if (page === 0) {
                setIsLoading(true);
            } else {
                setIsLoadingMore(true);
            }

            const response = await client.get(`products/${categoryPath}?page=${page}&size=${ITEMS_PER_PAGE}`);
            const { products, currentPage, totalPages, totalElements, hasMore } = response.data;

            if (reset || page === 0) {
                setAllProducts(products);
                setDisplayedProducts(products);
            } else {
                setAllProducts((prev) => [...prev, ...products]);
                setDisplayedProducts((prev) => [...prev, ...products]);
            }

            setCurrentPage(currentPage);
            setTotalPages(totalPages);
            setTotalElements(totalElements);
            setHasMore(hasMore);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    };

    const loadMoreProducts = useCallback(() => {
        if (isLoadingMore || !hasMore) return;
        const categoryPath = extractCategoryFromPath(location.pathname);
        const nextPage = currentPage + 1;
        fetchProducts(categoryPath, nextPage, false);
    }, [currentPage, hasMore, isLoadingMore, location.pathname]);

    useEffect(() => {
        const categoryPath = extractCategoryFromPath(location.pathname);

        if (categoryPath !== 'search') {
            setIsLoading(true);
            setCurrentPage(0);
            setHasMore(false);

            Promise.all([
                client.get('category/' + categoryPath),
                client.get(`products/${categoryPath}?page=0&size=${ITEMS_PER_PAGE}`),
            ])
                .then(([categoryResponse, productResponse]) => {
                    setCategories(categoryResponse.data);
                    const { products, currentPage, totalPages, totalElements, hasMore } = productResponse.data;
                    setAllProducts(products);
                    setDisplayedProducts(products);
                    setCurrentPage(currentPage);
                    setTotalPages(totalPages);
                    setTotalElements(totalElements);
                    setHasMore(hasMore);
                })
                .catch((error) => {
                    console.error('Error loading data:', error);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [location]);

    const setSearchResults = (searchResults: any) => {
        const products = searchResults.products || searchResults;
        setAllProducts(products);
        setDisplayedProducts(products);
        setCurrentPage(0);
        setTotalPages(1);
        setTotalElements(products.length);
        setHasMore(false);
    };

    return {
        categories,
        products: displayedProducts,
        allProducts,
        loadMoreProducts,
        hasMoreProducts: hasMore,
        isLoading,
        isLoadingMore,
        totalElements,
        currentPage: currentPage + 1,
        totalPages,
        setSearchResults,
    };
};

export default useCategoryData;